/** @fileoverview Collects and submits feedback for an assistant message. */

import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Field, FieldError, FieldGroup, FieldLabel } from '../ui/field';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import type { FeedbackDialogProps, FeedbackFormValues } from './types';

const feedbackSchema = (negative: boolean) =>
  z
    .object({
      category: z.string(),
      comment: z.string().trim().max(2000, 'Comment must be 2,000 characters or fewer.'),
    })
    .superRefine(({ category, comment }, context) => {
      if (!negative) return;
      if (!category && !comment) {
        context.addIssue({
          code: 'custom',
          path: ['category'],
          message: 'Choose a category or add a comment.',
        });
      }
      if (category === 'other' && !comment) {
        context.addIssue({
          code: 'custom',
          path: ['comment'],
          message: 'Add a comment for Other.',
        });
      }
    });

/** Displays the feedback modal and validates the required negative-feedback details. */
export default function FeedbackDialog({
  feedback,
  categories,
  saving,
  onSave,
  onClose,
}: FeedbackDialogProps) {
  const negative = feedback?.rating === 'negative';
  const schema = useMemo(() => feedbackSchema(negative), [negative]);
  const form = useForm<FeedbackFormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: { category: '', comment: '' },
  });

  useEffect(() => {
    form.reset({ category: '', comment: '' });
  }, [feedback?.id, feedback?.rating, form]);

  if (!feedback) return null;

  const submit = ({ category, comment }: FeedbackFormValues) => {
    onSave(feedback.rating, category, comment);
  };

  const categoryError = form.formState.errors.category;
  const commentError = form.formState.errors.comment;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md rounded-xl p-0">
        <form noValidate onSubmit={form.handleSubmit(submit)}>
          <DialogHeader className="p-6 pb-2">
            <DialogTitle className="text-base font-bold text-app-text">
              {negative ? 'How can this improve?' : 'What worked well?'}
            </DialogTitle>
          </DialogHeader>
          <FieldGroup className="gap-4 px-6">
            {negative && (
              <Field data-invalid={Boolean(categoryError)}>
                <FieldLabel htmlFor="feedback-category" className="text-xs text-app-text-secondary">
                  Category
                </FieldLabel>
                <Select
                  value={form.watch('category') || null}
                  onValueChange={(value) => {
                    form.setValue('category', value || '', { shouldValidate: true });
                  }}
                >
                  <SelectTrigger id="feedback-category" className="w-full text-xs">
                    <SelectValue placeholder="Choose a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((item) => (
                      <SelectItem key={item} value={item}>
                        {item.split('_').join(' ')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FieldError errors={[categoryError]} className="text-xs" />
              </Field>
            )}
            <Field data-invalid={Boolean(commentError)}>
              <FieldLabel htmlFor="feedback-comment" className="text-xs text-app-text-secondary">
                Comment
              </FieldLabel>
              <Textarea
                id="feedback-comment"
                className="min-h-24 text-xs"
                maxLength={2000}
                {...form.register('comment')}
              />
              <FieldError errors={[commentError]} className="text-xs" />
            </Field>
          </FieldGroup>
          <DialogFooter className="mt-6 rounded-b-xl px-6">
            <Button variant="outline" type="button" disabled={saving} onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-app-action text-app-action-foreground hover:bg-app-action-hover"
            >
              Save feedback
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
